import { addDays, parseISO } from 'date-fns';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Button, ButtonVariants } from '@components/button/Button';
import { ControlledFormDatePicker } from '@components/formInputs/ControlledFormDatePicker';
import { ControlledFormTabRadio } from '@components/formInputs/ControlledFormTabRadio';
import { ControlledFormTextareaAutosize } from '@components/formInputs/ControlledFormTextareaAutosize';
import { ControlledFormTimeSelect } from '@components/formInputs/ControlledFormTimeSelect';
import { LayoutVariant } from '@components/formInputs/sharedTypes';
import { Modal } from '@components/modal/Modal';
import { ModalActions } from '@components/modal/ModalActions';
import { toISODateFormat } from '@shared/utils/dateFormatters';
import styles from './BlockReservationsModal.scss';
import typography from '~styles/typography.scss';
import cx from 'classnames';

export interface BlockReservationsSubmitData {
  startDate: string;
  endDate: string;
  firstReservationTime?: string | null;
  lastReservationTime?: string | null;
  guestFacingReason?: string;
}

interface BlockReservationsModalProps {
  closeModal: () => void;
  onConfirm: (data: BlockReservationsSubmitData) => void;
  isOpen: boolean;
  reservationDate: Date;
}

export interface BlockReservationsFormData {
  startDate: string;
  endDate: string;
  firstReservationTime?: string | null;
  lastReservationTime?: string | null;
  guestFacingReason?: string;
  isMultiDay: boolean;
}

export const BlockReservationsModal = ({
  closeModal,
  onConfirm,
  isOpen,
  reservationDate,
}: BlockReservationsModalProps) => {
  const defaultDate = toISODateFormat(reservationDate);

  const { control, handleSubmit, watch, reset, setValue, getValues } =
    useForm<BlockReservationsFormData>({
      defaultValues: {
        startDate: defaultDate,
        endDate: defaultDate,
        firstReservationTime: undefined,
        lastReservationTime: undefined,
        guestFacingReason: '',
        isMultiDay: false,
      },
    });

  useEffect(() => {
    if (isOpen) {
      reset({
        startDate: defaultDate,
        endDate: defaultDate,
        firstReservationTime: undefined,
        lastReservationTime: undefined,
        guestFacingReason: '',
        isMultiDay: false,
      });
    }
  }, [isOpen, defaultDate, reset]);

  const isMultiDay = watch('isMultiDay');
  const startDate = watch('startDate');
  const firstReservationTime = watch('firstReservationTime');

  useEffect(() => {
    if (isMultiDay) {
      const nextDay = toISODateFormat(
        addDays(parseISO(getValues('startDate')), 1),
      );
      setValue('endDate', nextDay);
    }
  }, [isMultiDay, setValue, getValues]);

  const handleOnSubmit = (data: BlockReservationsFormData) => {
    const trimmedReason = data.guestFacingReason?.trim();
    const guestFacingReason = trimmedReason ? trimmedReason : undefined;
    if (data.isMultiDay) {
      onConfirm({
        startDate: data.startDate,
        endDate: data.endDate,
        firstReservationTime: null,
        lastReservationTime: null,
        guestFacingReason,
      });
    } else {
      onConfirm({
        startDate: data.startDate,
        endDate: data.startDate,
        firstReservationTime: data.firstReservationTime ?? null,
        lastReservationTime: data.lastReservationTime ?? null,
        guestFacingReason,
      });
    }
  };

  return isOpen ? (
    <Modal title="Block Reservations" isOpen={isOpen} onClose={closeModal}>
      <p className={cx(typography.c2, styles.modalSubtitle)}>
        Block new online reservations. No existing reservations will be
        canceled.
      </p>
      <form onSubmit={handleSubmit(handleOnSubmit)} className={styles.form}>
        <div className={styles.toggle}>
          <ControlledFormTabRadio
            control={control}
            name="isMultiDay"
            label="Closure Duration"
            options={['Single day', 'Multiple days']}
          />
        </div>
        {isMultiDay ? (
          <>
            <div className={styles.datePicker}>
              <ControlledFormDatePicker
                control={control}
                label="Start Date"
                name="startDate"
              />
            </div>
            <div className={styles.datePicker}>
              <ControlledFormDatePicker
                control={control}
                label="End Date"
                name="endDate"
                rules={{
                  validate: {
                    isAfterStartDate: (value) =>
                      value >= startDate ||
                      'End Date must be on or after Start Date.',
                  },
                }}
              />
            </div>
          </>
        ) : (
          <>
            <div className={styles.datePicker}>
              <ControlledFormDatePicker
                control={control}
                label="Date"
                name="startDate"
              />
            </div>
            <ControlledFormTimeSelect
              control={control}
              label="First Reservable Time"
              name="firstReservationTime"
              variant={LayoutVariant.Horizontal}
              rules={{ required: false }}
              className={styles.select}
            />
            <ControlledFormTimeSelect
              control={control}
              label="Last Reservable Time"
              name="lastReservationTime"
              variant={LayoutVariant.Horizontal}
              rules={{
                required: false,
                validate: {
                  isAfterFirstReservationTime: (
                    lastReservationTime: string | null | undefined,
                  ): string | boolean => {
                    if (!lastReservationTime || !firstReservationTime) {
                      return true;
                    }
                    return (
                      lastReservationTime > firstReservationTime ||
                      'Last Reservation Time must be after First Reservation Time.'
                    );
                  },
                },
              }}
              className={styles.select}
            />
          </>
        )}
        <ControlledFormTextareaAutosize
          control={control}
          label="Guest-Facing Reason for Closure"
          showOptionalSuffix
          name="guestFacingReason"
          placeholder="e.g., Closed for a remodel"
          rules={{
            validate: {
              minLength: (value: string | undefined) => {
                const trimmed = value?.trim() ?? '';
                return (
                  trimmed.length === 0 ||
                  trimmed.length >= 4 ||
                  'Reason must be at least 4 characters'
                );
              },
              maxLength: (value: string | undefined) => {
                const trimmed = value?.trim() ?? '';
                return (
                  trimmed.length <= 500 ||
                  'Reason must not exceed 500 characters'
                );
              },
            },
          }}
        />
        <p className={styles.subtext}>
          {isMultiDay
            ? 'Reservations will be blocked on every day in this range.'
            : 'Online reservations will be blocked from now until the end of the day if first and last reservable time have not been set.'}
        </p>
        <ModalActions className={styles.actions}>
          <Button
            label="Cancel"
            onClick={closeModal}
            variant={ButtonVariants.Tertiary}
          />
          <Button
            label="Block Reservations"
            type="submit"
            variant={ButtonVariants.Primary}
          />
        </ModalActions>
      </form>
    </Modal>
  ) : null;
};
